import { useCallback, useState } from "react";

import {
  alocacoesDaAlteracao,
  detalheAlteracaoIncluida,
  mensagemAlteracaoIncluida,
  detalheNovosConflitos,
  mensagemNovosConflitos,
  rotuloObra,
  MENSAGEM_INSTRUCAO_RECUSADA,
} from "@/modules/recursos/simulacao";
import type {
  AlteracaoSimulada,
  ConflitoNovo,
  InclusaoSimulada,
} from "@/modules/recursos/simulacao";
import type { ResultadoInclusao } from "../components/simulacao/resolucao-simulada";
import type { JustificativaConflitosDialogProps } from "../components/simulacao/justificativa-conflitos-dialog";

export interface SimulacaoNotice {
  severity: "success" | "warn" | "error";
  summary: string;
  detail?: string;
}

interface Pendente {
  alteracao: AlteracaoSimulada;
  conflitos: ConflitoNovo[];
  resolve: (resultado: ResultadoInclusao) => void;
  erro: string | null;
}

/** O mesmo conflito pode chegar por dois caminhos (dados da perspectiva e do modal). */
function unicos(conflitos: ConflitoNovo[]): ConflitoNovo[] {
  const vistos = new Set<string>();
  return conflitos.filter((conflito) => {
    const chave = JSON.stringify(conflito);
    if (vistos.has(chave)) return false;
    vistos.add(chave);
    return true;
  });
}

const OK: ResultadoInclusao = { ok: true };

function mensagemDaRecusa(error: unknown): string {
  return error instanceof Error && error.message ? error.message : MENSAGEM_INSTRUCAO_RECUSADA;
}

function contarPorRecurso(conflitos: ConflitoNovo[]): Record<string, number> {
  const total: Record<string, number> = {};
  for (const { recurso } of conflitos) total[recurso] = (total[recurso] ?? 0) + 1;
  return total;
}

/**
 * Inclusão de uma alteração no cenário, comum a Definir Equipe e Resolver Conflito.
 * Sem conflitos novos, inclui e avisa. Com eles (RN011), pede uma justificativa única
 * antes — a tela desenha o modal com `justificativa` —: Voltar devolve `mensagem: null` e nada
 * entra no cenário; Confirmar inclui com a justificativa guardada na alteração e avisa
 * quantos conflitos novos ela traz. Se o serviço recusa, o `incluir` rejeita com `Error`: a
 * mensagem vai no resultado (e no diálogo da justificativa, que segue aberto).
 */
export function useIncluirNaSimulacao(
  incluir: (alteracao: AlteracaoSimulada) => Promise<void> | void,
  notify: (notice: SimulacaoNotice) => void,
): {
  incluirNaSimulacao: (inclusao: InclusaoSimulada) => Promise<ResultadoInclusao>;
  justificativa: JustificativaConflitosDialogProps | null;
} {
  const [pendente, setPendente] = useState<Pendente | null>(null);

  const incluirNaSimulacao = useCallback(
    ({ alteracao, conflitosNovos }: InclusaoSimulada): Promise<ResultadoInclusao> => {
      const conflitos = unicos(conflitosNovos);
      if (conflitos.length === 0) {
        const r = incluir(alteracao);
        const notificar = () => {
          notify({
            severity: "success",
            summary: mensagemAlteracaoIncluida(alteracao),
            detail: detalheAlteracaoIncluida(alteracao),
          });
        };
        if (r instanceof Promise) {
          return r.then(
            (): ResultadoInclusao => {
              notificar();
              return OK;
            },
            (error: unknown): ResultadoInclusao => ({
              ok: false,
              mensagem: mensagemDaRecusa(error),
            }),
          );
        }
        notificar();
        return Promise.resolve(OK);
      }
      return new Promise((resolve) => setPendente({ alteracao, conflitos, resolve, erro: null }));
    },
    [incluir, notify],
  );

  const justificativa: JustificativaConflitosDialogProps | null = pendente
    ? {
        conflitos: pendente.conflitos,
        obraNova: "obra" in pendente.alteracao ? rotuloObra(pendente.alteracao.obra) : undefined,
        alocacoesNovas: alocacoesDaAlteracao(pendente.alteracao),
        erro: pendente.erro,
        onVoltar: () => {
          setPendente(null);
          pendente.resolve({ ok: false, mensagem: null });
        },
        onConfirmar: (texto) => {
          if (pendente.erro) setPendente({ ...pendente, erro: null });
          const inclusao = incluir({
            ...pendente.alteracao,
            justificativaConflitos: texto,
            conflitosResiduais: contarPorRecurso(pendente.conflitos),
          });
          const concluir = () => {
            setPendente(null);
            notify({
              severity: "warn",
              summary: mensagemNovosConflitos(pendente.conflitos.length),
              detail: detalheNovosConflitos(pendente.conflitos),
            });
            pendente.resolve(OK);
          };
          if (inclusao instanceof Promise) {
            void inclusao.then(concluir, (error: unknown) => {
              setPendente({ ...pendente, erro: mensagemDaRecusa(error) });
            });
            return;
          }
          concluir();
        },
      }
    : null;

  return { incluirNaSimulacao, justificativa };
}
